/**
 * AggSystemSummaryTable - Hierarchical table for Agg System drilldown
 */

import React from "react";
import { Table, TableBody, Paper, Typography } from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@material-ui/icons";
import { useExpandable } from "../../../hooks";
import { DataCells } from "../../molecules";
import {
  SummaryTableContainer,
  StickyFirstCell,
  IndentedCell,
  GroupRow,
  ChildRow,
  GrandchildRow,
  TotalsRow,
  ExpandIcon,
  NoDataMessage,
} from "../../styled";
import SummaryHeader from "./SummaryHeader";

/**
 * Agg System summary table component
 * @param {Object} props
 * @param {import("../../../types").AggSystemSummary} props.summary - Agg system summary data
 * @param {string} props.mode - Drilldown mode ("year-wells" or "wells-year")
 * @param {string} props.columnMode - Column display mode
 */
const AggSystemSummaryTable = ({ summary, mode, columnMode }) => {
  const { expandedL1, expandedL2, toggleExpandL1, toggleExpandL2 } =
    useExpandable(mode);

  if (!summary || summary.aggSystems.length === 0) {
    return (
      <NoDataMessage>
        <Typography variant="body1">No data for selected filters.</Typography>
      </NoDataMessage>
    );
  }

  const renderLevel2Rows = (aggSystem, child, childKey) => {
    const l2Key = `${aggSystem.agg_system_ndx}-${childKey}`;
    const isExpanded = expandedL2[l2Key];
    const grandchildren =
      mode === "year-wells" ? child.wells : child.years || [];
    const hasGrandchildren = grandchildren.length > 0;

    return (
      <React.Fragment key={l2Key}>
        <ChildRow
          onClick={(e) => {
            e.stopPropagation();
            if (hasGrandchildren) toggleExpandL2(l2Key);
          }}
          style={{ cursor: hasGrandchildren ? "pointer" : "default" }}
        >
          <IndentedCell $indent={3}>
            {hasGrandchildren && (
              <ExpandIcon>
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </ExpandIcon>
            )}
            {mode === "year-wells"
              ? child.year
              : `${child.cuwcd_well_number} - ${
                  child.state_well_number || ""
                } - ${child.well_name}`}
          </IndentedCell>
          <DataCells
            totals={child.totals}
            pctPermit={child.pct_permit}
            columnMode={columnMode}
          />
        </ChildRow>
        {isExpanded &&
          grandchildren.map((gc) => (
            <GrandchildRow
              key={`${l2Key}-${mode === "year-wells" ? gc.well_ndx : gc.year}`}
            >
              <IndentedCell $indent={5}>
                {mode === "year-wells"
                  ? `${gc.cuwcd_well_number} - ${
                      gc.state_well_number || ""
                    } - ${gc.well_name}`
                  : gc.year}
              </IndentedCell>
              <DataCells
                totals={gc.totals}
                pctPermit={gc.pct_permit}
                columnMode={columnMode}
              />
            </GrandchildRow>
          ))}
      </React.Fragment>
    );
  };

  return (
    <SummaryTableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <SummaryHeader
          firstColumnLabel="Aggregate System"
          columnMode={columnMode}
        />
        <TableBody>
          {summary.aggSystems.map((aggSystem) => (
            <React.Fragment key={aggSystem.agg_system_ndx}>
              <GroupRow
                onClick={() => toggleExpandL1(aggSystem.agg_system_ndx)}
              >
                <StickyFirstCell>
                  <ExpandIcon>
                    {expandedL1[aggSystem.agg_system_ndx] ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </ExpandIcon>
                  {aggSystem.agg_system_name}
                </StickyFirstCell>
                <DataCells
                  totals={aggSystem.totals}
                  pctPermit={aggSystem.pct_permit}
                  columnMode={columnMode}
                />
              </GroupRow>
              {expandedL1[aggSystem.agg_system_ndx] &&
                aggSystem.children.map((child) =>
                  renderLevel2Rows(
                    aggSystem,
                    child,
                    mode === "year-wells" ? child.year : child.well_ndx
                  )
                )}
            </React.Fragment>
          ))}
          <TotalsRow>
            <StickyFirstCell>
              <strong>Totals</strong>
            </StickyFirstCell>
            <DataCells
              totals={summary.grandTotals}
              pctPermit={summary.grandTotals.pct_permit}
              columnMode={columnMode}
            />
          </TotalsRow>
        </TableBody>
      </Table>
    </SummaryTableContainer>
  );
};

export default AggSystemSummaryTable;
